import { AvTelemetryClientContext } from 'src/app/features/video-conference/av-telemetry/models/av-telemetry.model';

/**
 * Chime-specific AV telemetry payload shapes.
 *
 * These are the RAW vendor shapes that ride inside AvTelemetryEnvelope.payload. They are
 * intentionally NOT part of the cross-track contract — a Vonage source ships Vonage's own
 * shapes, and the server-side per-vendor translator is what reconciles them.
 *
 * Chime's own vocabulary is preserved: `meetingId`, `attendeeId`, `mediaRegion` — not
 * renamed to neutral equivalents. Renaming is the translator's job, and doing it here would
 * discard information the translator may later need.
 */

/** Trailing window of Chime `meetingHistory` retained per event, per AWS guidance. */
export const CHIME_MEETING_HISTORY_WINDOW_MS = 120_000;

/**
 * `meetingId` on every record type, matching the agreed wire shape.
 *
 * The spine no longer carries session identity, so each payload names its own session. This
 * is what lets a chunk be attributed after arriving orphaned.
 */
export interface ChimeSessionRef {
  meetingId: string;
}

/**
 * One 1 Hz sample, verbatim from ClientMetricReport.
 *
 * Chime metric names are preserved exactly. Direction is already encoded in those names
 * (e.g. audioPacketsSentFractionLoss vs audioPacketsReceivedFractionLoss), so there is no
 * client-side regrouping into up/down — that grouping is the translator's job.
 */
export interface ChimeTickPayload extends ChimeSessionRef {
  /** Verbatim `ClientMetricReport.getObservableMetrics()`. */
  session: Record<string, number>;
  /**
   * Verbatim `ClientMetricReport.getObservableVideoMetrics()`.
   *
   * Keying is MIXED, confirmed against real session data: the same map carries both
   * attendeeId-shaped GUIDs and numeric SSRC keys. The SDK type comment ("for each SSRC")
   * and the AWS per-attendee guidance are each half right, so the server-side translator
   * must handle both shapes rather than assuming one.
   *
   * The value type is `unknown` rather than `number` because the SDK declares the inner
   * values as `{}`.
   */
  video: Record<string, Record<string, unknown>>;
}

/**
 * Session-constant context, emitted once as the first record of a session.
 *
 * Composes the shared browser context with Chime's session identity. `attendeeId` and
 * `mediaRegion` appear here only — they do not change mid-session, so repeating them on
 * every tick would be waste.
 */
export interface ChimeMetaPayload extends ChimeSessionRef, AvTelemetryClientContext {
  attendeeId: string;
  mediaRegion: string | null;
}

/**
 * A discrete event, recorded verbatim.
 *
 * `name` is Chime's own event name — NOT mapped to a ClearSign taxonomy here. Mapping to
 * EventTypeCode happens in the server-side evaluator.
 *
 * `history` is Chime's trailing `meetingHistory`, trimmed to a window.
 */
export interface ChimeEventPayload extends ChimeSessionRef {
  name: string;
  attributes: Record<string, unknown>;
  history?: ChimeHistoryEntry[];
}

export interface ChimeHistoryEntry {
  name: string;
  timestampMs: number;
}

/** Synthetic event names for AudioVideoObserver signals absent from `eventDidReceive`. */
export enum ChimeSyntheticEventName {
  ConnectionDidBecomePoor = 'connectionDidBecomePoor',
  ConnectionDidBecomeGood = 'connectionDidBecomeGood',
  /**
   * `MeetingSession.eventController` was absent, so no discrete events will arrive for this
   * session. Recorded in-band because otherwise the gap is indistinguishable downstream from
   * a session that genuinely had no disruptions. Metrics are unaffected.
   */
  EventControllerUnavailable = 'eventControllerUnavailable',
}
